
import { useEffect, useState } from "react";
import { SponsorsCarousel } from "@/components/SponsorsCarousel";
import { PatientJourney } from "@/components/PatientJourney";
import { Approaches } from "@/components/Approaches";
import { BlogCarousel } from "@/components/BlogCarousel";
import { InstagramFeed } from "@/components/InstagramFeed";
import { FAQ } from "@/components/FAQ";
import { Footer } from "@/components/Footer";
import { AccessibilityButton } from "@/components/AccessibilityButton";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { Header } from "@/components/Header";
import { HeroSection } from "@/components/HeroSection";
import { TestimonialsSection } from "@/components/TestimonialsSection";
import { CTASection } from "@/components/CTASection";
import { usePageVisibility, PAGE_VISIBILITY_KEYS } from "@/hooks/usePageVisibility";
import { settingsApi } from "@/integrations/mysql/client";

const DEFAULT_ORDER = ['jornada', 'diferenciais', 'blog', 'instagram', 'faq', 'depoimentos', 'cta'];

// Sections that always keep their own dark background — they don't participate in the light alternation
const DARK_SECTIONS = new Set(['diferenciais', 'depoimentos']);

// Two light background colors that alternate between visible "light" sections
const LIGHT_COLORS = ['bg-white', 'bg-[#f4fbf7]'] as const;

export function DesktopHome() {
  const { isVisible } = usePageVisibility();
  const [sectionOrder, setSectionOrder] = useState<string[]>(DEFAULT_ORDER);

  useEffect(() => {
    settingsApi.list().then(settings => {
      const raw = settings.find(s => s.key === 'home_sections_order')?.value;
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length > 0) setSectionOrder(parsed);
        } catch { /* keep default */ }
      }
    }).catch(() => { /* keep default */ });
  }, []);

  // Determine which sections are visible
  const isSectionVisible = (id: string): boolean => {
    switch (id) {
      case 'jornada':    return isVisible(PAGE_VISIBILITY_KEYS.jornada);
      case 'blog':       return isVisible(PAGE_VISIBILITY_KEYS.blog);
      case 'faq':        return isVisible(PAGE_VISIBILITY_KEYS.faq);
      case 'depoimentos': return isVisible(PAGE_VISIBILITY_KEYS.depoimentos);
      default:           return true;
    }
  };

  // Build ordered list of visible sections, assigning alternating bg to light sections
  const visibleSections: { id: string; bg: string }[] = [];
  let lightIdx = 0;

  for (const id of sectionOrder) {
    if (!isSectionVisible(id)) continue;
    if (DARK_SECTIONS.has(id)) {
      visibleSections.push({ id, bg: '' }); // dark sections own their bg
    } else {
      visibleSections.push({ id, bg: LIGHT_COLORS[lightIdx % 2] });
      lightIdx++;
    }
  }

  const renderSection = (id: string, bg: string) => {
    switch (id) {
      case 'jornada':
        return <div key="jornada"><PatientJourney className={bg} /></div>;
      case 'diferenciais':
        return <div key="diferenciais"><Approaches /></div>;
      case 'blog':
        return <div key="blog"><BlogCarousel className={bg} /></div>;
      case 'instagram':
        return <div key="instagram"><InstagramFeed className={bg} /></div>;
      case 'faq':
        return <div key="faq"><FAQ className={bg} /></div>;
      case 'depoimentos':
        return <div key="depoimentos"><TestimonialsSection /></div>;
      case 'cta':
        return <div key="cta"><CTASection className={bg} /></div>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white font-primary">
      <Header />
      <HeroSection />
      <SponsorsCarousel />
      {visibleSections.map(({ id, bg }) => renderSection(id, bg))}
      <Footer />
      <FloatingWhatsApp />
      <AccessibilityButton />
    </div>
  );
}
